import {
  ColDef,
  GetRowIdParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef, useState } from "react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Select from "src/components/common/gridWrapper/Grid_Select";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import {
  defaultColDef,
  getDefaultRowIndexColumnConfig,
} from "src/utils/grid/gridStyleUtils";
import {
  defaultPDA1030_R01Dto,
  PDA1030_R01Dto,
} from "./PDA1030_R01Dto";
import usePDA1030_R01Store from "./Store_PDA1030_R01";

const COLUMN_STATE_KEY = "PDA1030_R01_ColumnState";
const HEADER_NAMES: Record<string, string> = {
  rowIndex: "",
  customNm: "상호",
  itemNm: "품명",
  itemAmt: "매출금액",
};
const PDA1030_R01Table = () => {
  const PDA1030_R01List = usePDA1030_R01Store(
    (state) => state.PDA1030_R01List
  );
  const columnState = usePDA1030_R01Store((state) => state.columnState);
  const setColumnState = usePDA1030_R01Store(
    (state) => state.setColumnState
  );
  const scrollTop = usePDA1030_R01Store((state) => state.scrollTop);
  const setScrollTop = usePDA1030_R01Store((state) => state.setScrollTop);
  const gridRef = useRef<AgGridReact<PDA1030_R01Dto>>(null);
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const [, setHoveredRowIndex] = useState<number | null>(null);

  const { createRowClassRules, paddedGridData } = useGridSettingContent({
    gridRef,
    key: "salBillNo",
    gridData: PDA1030_R01List,
    defaultGridData: defaultPDA1030_R01Dto,
    selectedRow: undefined,
  });

  const getCellStyle = useCallback(
    (params: unknown, key: string) =>
      onlyTouchGridStyle(key, "customNm")(params),
    []
  );

  const totalItemAmt = useMemo(
    () =>
      PDA1030_R01List.reduce(
        (total, row) => total + Number(row.itemAmt ?? 0),
        0
      ),
    [PDA1030_R01List]
  );

  const pinnedBottomRowData = useMemo<PDA1030_R01Dto[]>(
    () => [
      {
        ...defaultPDA1030_R01Dto,
        itemAmt: totalItemAmt,
      },
    ],
    [totalItemAmt]
  );

  const baseColumnDefs = useMemo<ColDef<PDA1030_R01Dto>[]>(
    () => [
      ...getDefaultRowIndexColumnConfig(),
      {
        headerName: "상호",
        colId: "customNm",
        field: "customNm",
        cellStyle: (params) => getCellStyle(params, "customNm"),
        width: 70,
      },
      {
        headerName: "품명",
        colId: "itemNm",
        field: "itemNm",
        cellStyle: (params) => getCellStyle(params, "itemNm"),
        width: 90,
      },
      {
        headerName: "매출금액",
        colId: "itemAmt",
        field: "itemAmt",
        cellStyle: (params) => getCellStyle(params, "itemAmt"),
        comparator: (valueA, valueB) => (valueA || 0) - (valueB || 0),
        valueFormatter: (params) => params.value?.toLocaleString() ?? "",
        width: 110,
      },
    ],
    [getCellStyle]
  );

  const defaultColumnState = useMemo(
    () => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
    [baseColumnDefs]
  );

  const columnDefs = useMemo(
    () =>
      columnState === null
        ? baseColumnDefs
        : GridCustomColumnDefs<PDA1030_R01Dto>({
            baseColumnDefs,
            columnState,
            headerNames: HEADER_NAMES,
          }),
    [baseColumnDefs, columnState]
  );

  const getRowId = useCallback(
    (params: GetRowIdParams<PDA1030_R01Dto>) =>
      `${params.data.salBillNo}_${params.data.itemNo}`,
    []
  );

  return (
    <div className="flex-1 p-2 flex w-full h-full flex-col overflow-hidden">
      <GridContextWrapper<PDA1030_R01Dto>
        gridRef={gridRef}
        gridWrapperRef={gridWrapperRef}
        enableCellCalculation
        paddedGridData={paddedGridData}
        defaultRowData={defaultPDA1030_R01Dto}
        baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
        defaultColumnState={defaultColumnState}
        setColumnState={setColumnState}
        localStorageKey={COLUMN_STATE_KEY}
        menuItems={undefined}
      >
        <Grid_Select<PDA1030_R01Dto>
          ref={gridRef}
          rowData={paddedGridData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={getRowId}
          rowClassRules={createRowClassRules<PDA1030_R01Dto>({
            idKey: "salBillNo",
            selectedList: [],
            deletedList: [],
          })}
          onRowClicked={undefined}
          onCellKeyDown={undefined}
          setHoveredRowIndex={setHoveredRowIndex}
          columnStateKey={COLUMN_STATE_KEY}
          setColumnState={setColumnState}
          pinnedBottomRowData={pinnedBottomRowData}
          scrollTop={scrollTop}
          setScrollTop={setScrollTop}
        />
      </GridContextWrapper>
    </div>
  );
};

export default PDA1030_R01Table;
